// --- START OF FILE: src/components/RewardCapturePage.js ---
import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  TextField,
  Button,
  Box,
  Paper,
  CircularProgress,
  Alert,
  createTheme,
  ThemeProvider,
} from '@mui/material';
import { motion } from 'framer-motion';
import axios from 'axios';

const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#3B82F6',
    },
    secondary: {
      main: '#EC4899',
    },
    background: {
      default: '#0F172A',
      paper: 'rgba(255, 255, 255, 0.05)',
    },
    text: {
      primary: '#F8FAFC',
      secondary: '#CBD5E1',
    },
  },
  typography: {
    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  },
  shape: {
    borderRadius: 16,
  },
});

// Animated background matching HomePage
const AnimatedBackground = () => (
  <Box sx={{
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    overflow: 'hidden',
    zIndex: 0,
    background: 'radial-gradient(ellipse at top left, #1e3a8a 0%, #0f172a 50%, #0f172a 100%)',
  }} />
);

const RewardCapturePage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  
  const { clientId, tagId, redirectUrl, assignedTo } = location.state || {};

  const [contactInfo, setContactInfo] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);

  // FIXED: Updated port to 5002 to match the prolifiapp-backend app.js configuration
  const baseUrl = window.location.hostname.includes('truemetric.io')
    ? 'https://truemetric.io'
    : 'http://208.109.244.166:5002';

  useEffect(() => {
    if (!clientId || !redirectUrl) {
      navigate('/');
    }
  }, [clientId, redirectUrl, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!contactInfo.trim()) {
      setErrorMsg('Please enter your phone number or email.');
      return;
    }

    setIsSubmitting(true);
    setErrorMsg(null);

    try {
      // Call the backend endpoint to generate the secure coupon
      const response = await axios.post(`${baseUrl}/api/rewards/generate`, {
        clientId,
        tagId,
        assignedTo,
        contactInfo: contactInfo.trim(),
      });

      const { couponCode } = response.data;

      navigate('/thank-you', {
        state: {
          clientId,
          reviewUrl: redirectUrl,
          couponCode: couponCode,
          contactInfo: contactInfo.trim()
        }
      });
      
    } catch (error) {
      console.error('Error generating reward:', error);
      
      // If the backend returns a 429 (Cooldown limit reached), display that specific message
      if (error.response && error.response.status === 429) {
        setErrorMsg('Limit reached. Only one reward can be claimed per week.');
        setIsSubmitting(false);
        return;
      }

      // Otherwise, fallback and proceed to Thank You page so we don't lose the Google Review
      navigate('/thank-you', {
        state: {
          clientId,
          reviewUrl: redirectUrl,
          couponCode: 'PROMO-ERROR-500', 
          errorFallback: true
        }
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!clientId) return null; 

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ minHeight: '100vh', position: 'relative', overflowX: 'hidden' }}>
        <AnimatedBackground />
        
        <Container 
          sx={{ 
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            zIndex: 1,
            padding: { xs: '16px', sm: '24px' }
          }}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.5, type: "spring" }}
            style={{ width: '100%', maxWidth: '500px' }}
          >
            <Paper
              elevation={0}
              sx={{
                padding: { xs: '30px 20px', sm: '40px 30px' },
                borderRadius: '24px',
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                textAlign: 'center',
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
              }}
            >
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#F8FAFC', mb: 2 }}>
                Claim Your Reward! 🎁
              </Typography>
              
              <Typography variant="body1" sx={{ color: '#CBD5E1', mb: 4, lineHeight: 1.6 }}>
                We're thrilled you had a great experience! Where should we send your coupon?
              </Typography>

              {errorMsg && (
                <Alert severity="error" sx={{ mb: 3, backgroundColor: 'rgba(239, 68, 68, 0.1)', color: '#FCA5A5' }}>
                  {errorMsg}
                </Alert>
              )}

              <form onSubmit={handleSubmit}>
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="Enter Phone Number or Email"
                  value={contactInfo}
                  onChange={(e) => setContactInfo(e.target.value)}
                  disabled={isSubmitting}
                  sx={{
                    mb: 3,
                    '& .MuiOutlinedInput-root': {
                      color: '#F8FAFC',
                      backgroundColor: 'rgba(255, 255, 255, 0.05)',
                      borderRadius: '12px',
                      '& fieldset': {
                        borderColor: 'rgba(255, 255, 255, 0.2)',
                      },
                      '&:hover fieldset': {
                        borderColor: '#3B82F6',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#3B82F6',
                      },
                    },
                  }}
                />

                <Button
                  type="submit"
                  fullWidth
                  disabled={isSubmitting || !contactInfo.trim()}
                  sx={{
                    py: 1.8,
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    borderRadius: '12px',
                    textTransform: 'none',
                    background: 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)',
                    color: '#fff',
                    boxShadow: '0 10px 15px -3px rgba(59, 130, 246, 0.3)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #60A5FA 0%, #3B82F6 100%)',
                    },
                    '&:disabled': {
                      background: 'rgba(255, 255, 255, 0.1)',
                      color: 'rgba(255, 255, 255, 0.3)',
                    }
                  }}
                >
                  {isSubmitting ? <CircularProgress size={24} color="inherit" /> : 'Send My Reward'}
                </Button>
              </form>

              <Typography variant="caption" sx={{ display: 'block', mt: 3, color: '#64748B' }}>
                By continuing, you agree to receive messages regarding your reward.
              </Typography>
            </Paper>
          </motion.div>
        </Container>
      </Box>
    </ThemeProvider>
  );
};

export default RewardCapturePage;
// --- END OF FILE: src/components/RewardCapturePage.js ---